import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import useDebounce from "@/hooks/useDebounce";
import {
  TROUBLESHOOTING_SEARCH_MIN_CHARS,
  useTroubleshootingParticipantSearch,
} from "@/hooks/useTroubleshooting";
import { ParticipantSearchItem } from "@/domain/troubleshooting.interface";

export const SEARCH_DEBOUNCE_MS = 300;

interface ParticipantSearchProps {
  onSelect: (participant: ParticipantSearchItem) => void;
}

const fullName = (p: ParticipantSearchItem) =>
  `${p.firstName || ""} ${p.lastName || ""}`.trim();

/**
 * COYN-1507: participant search within the selected account — by name,
 * email, CIN or id (backend `GET /troubleshooting/participants/search`).
 */
const ParticipantSearch: React.FC<ParticipantSearchProps> = ({ onSelect }) => {
  const { t } = useTranslation("troubleshooting");
  const [input, setInput] = useState("");
  const debounced = useDebounce({ value: input, delay: SEARCH_DEBOUNCE_MS });
  const term = debounced.trim();
  const tooShort = term.length < TROUBLESHOOTING_SEARCH_MIN_CHARS;
  const { data, isFetching, isError } =
    useTroubleshootingParticipantSearch(term);
  const options = tooShort ? [] : data || [];

  const noOptionsText = tooShort
    ? t("search.minChars", { min: TROUBLESHOOTING_SEARCH_MIN_CHARS })
    : isError
      ? t("search.error")
      : t("search.noResults");

  return (
    <Autocomplete<ParticipantSearchItem, false, false, false>
      id="troubleshooting-participant-search"
      sx={{ minWidth: 320, maxWidth: 560, flex: 1 }}
      options={options}
      // Results are already filtered server-side.
      filterOptions={(x) => x}
      getOptionLabel={(option) => fullName(option)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={!tooShort && isFetching}
      noOptionsText={noOptionsText}
      value={null}
      inputValue={input}
      blurOnSelect
      clearOnBlur={false}
      onInputChange={(_, value, reason) => {
        if (reason !== "reset") setInput(value);
      }}
      onChange={(_, value) => {
        if (value) {
          onSelect(value);
          setInput("");
        }
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        const secondary = [option.cin, option.email, option.status]
          .filter(Boolean)
          .join(" · ");
        return (
          <Box component="li" key={key} {...rest}>
            <Box>
              <Typography variant="body1">{fullName(option)}</Typography>
              {secondary && (
                <Typography variant="caption" color="text.secondary">
                  {secondary}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("search.label")}
          placeholder={t("search.placeholder")}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <SearchIcon
                  aria-hidden
                  sx={{ color: "text.secondary", mr: 1 }}
                />
              ),
              endAdornment: (
                <>
                  {!tooShort && isFetching ? (
                    <CircularProgress size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default ParticipantSearch;
